/**
 * LeaseForm - Unified lease contract create/edit form
 *
 * Full form with always-visible core sections + advanced toggle.
 * No buttons — external submit via formId.
 * Used exclusively inside LeaseMutateDrawer.
 */

import { useEffect, useState, useMemo } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  ChevronDown,
  FileText,
  DollarSign,
  Fuel,
  Settings2,
  TrendingUp,
  RefreshCw,
  ArrowLeftRight,
  Shield,
  Plane,
  FileSignature,
} from 'lucide-react'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { SearchableSelect } from '@/components/ui/searchable-select'
import type { SearchableSelectOption, SearchableSelectGroup } from '@/components/ui/searchable-select'
import { cn } from '@/lib/utils'
import { leaseFormSchema, leaseFormDefaults } from './lease-form-schema'
import type { LeaseFormInput, LeaseFormValues } from './lease-form-schema'
import { leaseRowToFormDefaults } from './lease-form-utils'
import type { LeasePrefillData } from './LeaseMutateDrawer'
import { ContractAircraftSelect } from './ContractAircraftSelect'
import { useLocations } from '@/hooks/useLocations'
import { useClients } from '@/hooks/useClients'
import { useAllContacts } from '@/hooks/useContacts'
import { useUnits } from '@/hooks/useUnits'
import type { LeaseTableRow, LeaseType, LeaseStatus, EscalationType, FuelType, FuelPriceType } from '@/types/leases'
import { LEASE_TYPES, LEASE_TYPE_LABELS, LEASE_STATUSES, LEASE_STATUS_LABELS, ESCALATION_TYPES, ESCALATION_TYPE_LABELS, FUEL_TYPES, FUEL_TYPE_LABELS, FUEL_PRICE_TYPES, FUEL_PRICE_TYPE_LABELS } from '@/types/leases'
import { getClientDisplayName } from '@/types/clients'

// ============================================
// Types
// ============================================

export interface SelectedAircraft {
  id: string
  tailNumber: string
  typeName?: string | null
  isPrimary: boolean
}

interface LeaseFormProps {
  /** Existing lease data (snake_case database format) */
  lease?: LeaseTableRow | null
  /** Pre-computed form defaults for renewal (overrides lease data) */
  renewalDefaults?: LeaseFormInput | null
  /** Form ID for external submit button */
  formId?: string
  /** Is editing vs creating */
  isEditing?: boolean
  /** Called when form submitted with valid data */
  onSubmit?: (data: LeaseFormValues, aircraft: SelectedAircraft[]) => void | Promise<void>
  /** External loading state */
  isLoading?: boolean
  /** Disabled state */
  disabled?: boolean
  /** Pre-loaded contract aircraft (edit mode) */
  contractAircraft?: SelectedAircraft[]
  /** Lock location (pre-filled, disabled). Used from ramp view. */
  lockedLocationId?: string
  /** Pre-fill unit, client, contact from allocation context (create mode only). */
  prefillData?: LeasePrefillData
}

// ============================================
// Collapsible Section Helper
// ============================================

function SectionHeader({
  icon: Icon,
  title,
  open,
}: {
  icon: React.ComponentType<{ className?: string }>
  title: string
  open: boolean
}) {
  return (
    <CollapsibleTrigger asChild>
      <button
        type="button"
        className="flex w-full items-center gap-2 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
      >
        <Icon className="h-4 w-4" />
        <span className="flex-1 text-left">{title}</span>
        <ChevronDown
          className={cn(
            'h-4 w-4 transition-transform',
            open && 'rotate-180'
          )}
        />
      </button>
    </CollapsibleTrigger>
  )
}

// ============================================
// Component
// ============================================

export function LeaseForm({
  lease,
  renewalDefaults,
  formId,
  isEditing = false,
  onSubmit,
  isLoading = false,
  disabled = false,
  contractAircraft,
  lockedLocationId,
  prefillData,
}: LeaseFormProps) {
  // Compute initial form values from available data
  // renewalDefaults takes priority (pre-computed for renewal scenario)
  const computedDefaults = useMemo(
    () => renewalDefaults ?? (lease ? leaseRowToFormDefaults(lease) : leaseFormDefaults),
    [renewalDefaults, lease]
  )

  // Form setup
  const form = useForm<LeaseFormInput>({
    resolver: zodResolver(leaseFormSchema) as any,
    defaultValues: computedDefaults,
  })

  // Reset when data changes (e.g. lease loaded async)
  useEffect(() => {
    const newDefaults = renewalDefaults ?? (lease ? leaseRowToFormDefaults(lease) : leaseFormDefaults)
    form.reset(newDefaults)
  }, [renewalDefaults, lease, form])

  // Set locked location on mount
  useEffect(() => {
    if (lockedLocationId) {
      form.setValue('location_id', lockedLocationId)
    }
  }, [lockedLocationId, form])

  // Apply prefill data from allocation context (create mode only)
  useEffect(() => {
    if (!prefillData || isEditing) return
    if (prefillData.client_id) {
      form.setValue('client_id', prefillData.client_id)
    }
    if (prefillData.contact_id) {
      form.setValue('contact_id', prefillData.contact_id)
    }
    if (prefillData.unit_id) {
      form.setValue('unit_id', prefillData.unit_id)
    }
  }, [prefillData, isEditing, form])

  // ---------- State ----------
  const [selectedAircraft, setSelectedAircraft] = useState<SelectedAircraft[]>(
    []
  )
  const [showAdvanced, setShowAdvanced] = useState(false)

  // Collapsible section states (advanced sections)
  const [openSections, setOpenSections] = useState({
    term: true,
    escalation: false,
    renewal: false,
    transient: false,
    guarantees: false,
    aircraft: true,
    documents: false,
  })

  // Load contract aircraft when prop changes
  useEffect(() => {
    if (contractAircraft) {
      setSelectedAircraft(contractAircraft)
    }
  }, [contractAircraft])

  // Reset aircraft when not editing
  useEffect(() => {
    if (!isEditing) {
      setSelectedAircraft([])
    }
  }, [isEditing])

  // Watched values for cascading dropdowns
  const selectedLocationId = useWatch({
    control: form.control,
    name: 'location_id',
  })
  const selectedClientId = useWatch({
    control: form.control,
    name: 'client_id',
  })
  const escalationType = useWatch({
    control: form.control,
    name: 'escalation_type',
  })
  const allowTransient = useWatch({
    control: form.control,
    name: 'allow_transient_sublease',
  })

  // Data fetching for cascading dropdowns
  const { data: locations, isLoading: isLoadingLocations } = useLocations()
  const { data: clients, isLoading: isLoadingClients } = useClients()
  const { data: contacts, isLoading: isLoadingContacts } = useAllContacts()
  const { data: units, isLoading: isLoadingUnits } = useUnits()

  // Filtered lists
  const filteredContacts = useMemo(() => {
    if (!contacts || !selectedClientId) return []
    return contacts.filter((c) => c.client_id === selectedClientId)
  }, [contacts, selectedClientId])

  const filteredUnits = useMemo(() => {
    if (!units || !selectedLocationId) return []
    return units.filter((u) => u.location_id === selectedLocationId)
  }, [units, selectedLocationId])

  // Options for searchable selects
  const locationOptions: SearchableSelectOption[] = useMemo(
    () => locations?.map((loc) => ({ value: loc.id, label: loc.name })) ?? [],
    [locations]
  )

  const unitGroups: SearchableSelectGroup[] = useMemo(() => {
    const grouped = new Map<string, SearchableSelectOption[]>()
    for (const unit of filteredUnits) {
      const areaLabel = unit.area_name || 'Other'
      if (!grouped.has(areaLabel)) grouped.set(areaLabel, [])
      grouped.get(areaLabel)!.push({ value: unit.id, label: unit.unit_code })
    }
    return Array.from(grouped, ([label, options]) => ({ label, options }))
  }, [filteredUnits])

  const clientOptions: SearchableSelectOption[] = useMemo(
    () => clients?.map((c) => ({ value: c.id, label: getClientDisplayName(c) })) ?? [],
    [clients]
  )

  const contactOptions: SearchableSelectOption[] = useMemo(
    () =>
      filteredContacts.map((c) => ({
        value: c.id,
        label: `${c.first_name} ${c.last_name}`,
      })),
    [filteredContacts]
  )

  // ---------- Submit handler ----------
  const handleSubmit = async (values: LeaseFormValues) => {
    if (onSubmit) {
      await onSubmit(values, selectedAircraft)
    }
  }

  const isSubmitting = disabled

  const toggleSection = (section: keyof typeof openSections) => {
    setOpenSections((prev) => ({ ...prev, [section]: !prev[section] }))
  }

  // Wait for both external loading AND dropdown data to be ready
  const isDropdownDataLoading =
    isLoadingLocations || isLoadingClients || isLoadingContacts || isLoadingUnits

  if (isLoading || isDropdownDataLoading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    )
  }

  // ============================================
  // FORM
  // ============================================
  return (
    <Form {...form}>
      <form
        id={formId}
        onSubmit={form.handleSubmit(handleSubmit) as any}
        className="px-4 py-4 space-y-0"
      >
        {/* ── BASIC INFO ── */}
        <div className="space-y-4 pb-5">
          <h3 className="text-sm font-medium text-muted-foreground">Basic Info</h3>

          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control as any}
              name="location_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Location *</FormLabel>
                  <FormControl>
                    <SearchableSelect
                      options={locationOptions}
                      value={field.value || null}
                      onValueChange={(val) => {
                        field.onChange(val ?? '')
                        form.setValue('unit_id', '')
                      }}
                      placeholder="Select location"
                      searchPlaceholder="Search locations..."
                      emptyMessage="No locations found."
                      disabled={!!lockedLocationId}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control as any}
              name="unit_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Unit *</FormLabel>
                  <FormControl>
                    <SearchableSelect
                      groups={unitGroups}
                      value={field.value || null}
                      onValueChange={(val) => field.onChange(val ?? '')}
                      placeholder={selectedLocationId ? 'Select unit' : 'Select location first'}
                      searchPlaceholder="Search units..."
                      emptyMessage="No units found."
                      disabled={!selectedLocationId}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control as any}
              name="client_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Client *</FormLabel>
                  <FormControl>
                    <SearchableSelect
                      options={clientOptions}
                      value={field.value || null}
                      onValueChange={(val) => {
                        field.onChange(val ?? '')
                        form.setValue('contact_id', null)
                      }}
                      placeholder="Select client"
                      searchPlaceholder="Search clients..."
                      emptyMessage="No clients found."
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control as any}
              name="contact_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Contact</FormLabel>
                  <FormControl>
                    <SearchableSelect
                      options={contactOptions}
                      value={field.value || null}
                      onValueChange={(val) => field.onChange(val)}
                      placeholder="Select contact"
                      searchPlaceholder="Search contacts..."
                      emptyMessage="No contacts found."
                      disabled={!selectedClientId}
                      allowClear
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        {/* ── LEASE CONTRACT DETAILS ── */}
        <div className="border-t pt-5 pb-5 space-y-4">
          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <FileText className="h-4 w-4" />
            Lease Contract Details
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control as any}
              name="start_date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Start Date *</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control as any}
              name="end_date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>End Date (Optional)</FormLabel>
                  <FormControl>
                    <Input
                      type="date"
                      {...field}
                      value={field.value ?? ''}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control as any}
              name="type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Lease Type</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {LEASE_TYPES.map((type) => (
                        <SelectItem key={type} value={type}>
                          {LEASE_TYPE_LABELS[type as LeaseType]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control as any}
              name="status"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Status *</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {LEASE_STATUSES.map((status) => (
                        <SelectItem key={status} value={status}>
                          {LEASE_STATUS_LABELS[status as LeaseStatus]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        {/* ── PRICING ── */}
        <div className="border-t pt-5 pb-5 space-y-4">
          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <DollarSign className="h-4 w-4" />
            Pricing
          </div>

          <FormField
            control={form.control as any}
            name="monthly_rate"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Monthly Rate ($) *</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {/* rate_basis hidden temporarily */}

          <FormField
            control={form.control as any}
            name="security_deposit"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Security Deposit ($)</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="Optional"
                    {...field}
                    value={field.value ?? ''}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* ── FUEL ALLOWANCE ── */}
        <div className="border-t pt-5 pb-5 space-y-4">
          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <Fuel className="h-4 w-4" />
            Fuel Allowance (Optional)
          </div>

          {/* Row 1: Fuel Type */}
          <FormField
            control={form.control as any}
            name="fuel_type"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Fuel Type</FormLabel>
                <Select
                  onValueChange={(v) => field.onChange(v === '__none__' ? null : v)}
                  value={field.value ?? '__none__'}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="__none__">None</SelectItem>
                    {FUEL_TYPES.map((type) => (
                      <SelectItem key={type} value={type}>
                        {FUEL_TYPE_LABELS[type as FuelType]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Row 2: Monthly + Annual Commitment */}
          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control as any}
              name="fuel_commitment_gallons_monthly"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Monthly Commitment (gal)</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      placeholder="e.g., 500"
                      {...field}
                      value={field.value ?? ''}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control as any}
              name="fuel_commitment_gallons_annual"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Annual Commitment (gal)</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      placeholder="e.g., 6000"
                      {...field}
                      value={field.value ?? ''}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Row 3: Price Type + Price/Gallon + Differential */}
          <div className="grid grid-cols-3 gap-4">
            <FormField
              control={form.control as any}
              name="fuel_price_type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Price Type</FormLabel>
                  <Select
                    onValueChange={(v) => field.onChange(v === '__none__' ? null : v)}
                    value={field.value ?? '__none__'}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="__none__">None</SelectItem>
                      {FUEL_PRICE_TYPES.map((type) => (
                        <SelectItem key={type} value={type}>
                          {FUEL_PRICE_TYPE_LABELS[type as FuelPriceType]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control as any}
              name="fuel_price_per_gallon"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Price/Gallon ($)</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="e.g., 5.50"
                      {...field}
                      value={field.value ?? ''}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control as any}
              name="fuel_price_differential"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Differential ($)</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="e.g., 0.25"
                      {...field}
                      value={field.value ?? ''}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        {/* ── ADVANCED TOGGLE ── */}
        <div className="border-t pt-4 pb-2">
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <Checkbox
              checked={showAdvanced}
              onCheckedChange={(checked) => setShowAdvanced(!!checked)}
            />
            <Settings2 className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">Show advanced options</span>
          </label>
        </div>

        {/* ── ADVANCED SECTIONS ── */}
        {showAdvanced && (
          <div className="space-y-0">
            {/* Term */}
            <div className="border-t">
              <Collapsible
                open={openSections.term}
                onOpenChange={() => toggleSection('term')}
              >
                <SectionHeader
                  icon={FileText}
                  title="Term Details"
                  open={openSections.term}
                />
                <CollapsibleContent className="space-y-4 pb-4">
                  <FormField
                    control={form.control as any}
                    name="term_months"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Term (Months)</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            placeholder="e.g., 12"
                            {...field}
                            value={field.value ?? ''}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CollapsibleContent>
              </Collapsible>
            </div>

            {/* Escalation */}
            <div className="border-t">
              <Collapsible
                open={openSections.escalation}
                onOpenChange={() => toggleSection('escalation')}
              >
                <SectionHeader
                  icon={TrendingUp}
                  title="Escalation"
                  open={openSections.escalation}
                />
                <CollapsibleContent className="space-y-4 pb-4">
                  <FormField
                    control={form.control as any}
                    name="escalation_type"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Escalation Type</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value ?? 'none'}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {ESCALATION_TYPES.map((type) => (
                              <SelectItem key={type} value={type}>
                                {ESCALATION_TYPE_LABELS[type as EscalationType]}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  {escalationType === 'fixed_pct' && (
                    <div className="grid grid-cols-2 gap-4">
                      <FormField
                        control={form.control as any}
                        name="escalation_pct"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Escalation %</FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                step="0.01"
                                placeholder="e.g., 3.0"
                                {...field}
                                value={field.value ?? ''}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control as any}
                        name="escalation_date"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Escalation Date</FormLabel>
                            <FormControl>
                              <Input
                                type="date"
                                {...field}
                                value={field.value ?? ''}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  )}
                </CollapsibleContent>
              </Collapsible>
            </div>

            {/* Renewal */}
            <div className="border-t">
              <Collapsible
                open={openSections.renewal}
                onOpenChange={() => toggleSection('renewal')}
              >
                <SectionHeader
                  icon={RefreshCw}
                  title="Renewal Options"
                  open={openSections.renewal}
                />
                <CollapsibleContent className="space-y-4 pb-4">
                  <FormField
                    control={form.control as any}
                    name="auto_renew"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel>Auto-renew</FormLabel>
                          <FormDescription>
                            Automatically renew the lease if not cancelled
                          </FormDescription>
                        </div>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control as any}
                    name="renewal_notice_days"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Renewal Notice (Days)</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            placeholder="30"
                            {...field}
                            value={field.value ?? ''}
                          />
                        </FormControl>
                        <FormDescription>
                          Days before end date to notify for renewal
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CollapsibleContent>
              </Collapsible>
            </div>

            {/* Transient Sublease */}
            <div className="border-t">
              <Collapsible
                open={openSections.transient}
                onOpenChange={() => toggleSection('transient')}
              >
                <SectionHeader
                  icon={ArrowLeftRight}
                  title="Transient Sublease"
                  open={openSections.transient}
                />
                <CollapsibleContent className="space-y-4 pb-4">
                  <FormField
                    control={form.control as any}
                    name="allow_transient_sublease"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel>Allow Transient Sublease</FormLabel>
                          <FormDescription>
                            Space can be used by transients when aircraft is away
                          </FormDescription>
                        </div>
                      </FormItem>
                    )}
                  />
                  {allowTransient && (
                    <FormField
                      control={form.control as any}
                      name="transient_revenue_share_pct"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Revenue Share %</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              step="0.01"
                              placeholder="e.g., 50"
                              {...field}
                              value={field.value ?? ''}
                            />
                          </FormControl>
                          <FormDescription>
                            Percentage of transient revenue shared with lessee
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}
                </CollapsibleContent>
              </Collapsible>
            </div>

            {/* Guarantees */}
            <div className="border-t">
              <Collapsible
                open={openSections.guarantees}
                onOpenChange={() => toggleSection('guarantees')}
              >
                <SectionHeader
                  icon={Shield}
                  title="Guarantees"
                  open={openSections.guarantees}
                />
                <CollapsibleContent className="space-y-4 pb-4">
                  <FormField
                    control={form.control as any}
                    name="placement_guarantee"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel>Placement Guarantee</FormLabel>
                          <FormDescription>
                            Aircraft is guaranteed this specific spot
                          </FormDescription>
                        </div>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control as any}
                    name="requires_unblocked_access"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel>Requires Unblocked Access</FormLabel>
                          <FormDescription>
                            Aircraft must have free exit access at all times
                          </FormDescription>
                        </div>
                      </FormItem>
                    )}
                  />
                </CollapsibleContent>
              </Collapsible>
            </div>

            {/* Aircraft */}
            <div className="border-t">
              <Collapsible
                open={openSections.aircraft}
                onOpenChange={() => toggleSection('aircraft')}
              >
                <SectionHeader
                  icon={Plane}
                  title="Aircraft"
                  open={openSections.aircraft}
                />
                <CollapsibleContent className="space-y-4 pb-4">
                  <ContractAircraftSelect
                    value={selectedAircraft}
                    onChange={setSelectedAircraft}
                    disabled={isSubmitting}
                  />
                </CollapsibleContent>
              </Collapsible>
            </div>

            {/* Documents & Notes */}
            <div className="border-t">
              <Collapsible
                open={openSections.documents}
                onOpenChange={() => toggleSection('documents')}
              >
                <SectionHeader
                  icon={FileSignature}
                  title="Documents & Notes"
                  open={openSections.documents}
                />
                <CollapsibleContent className="space-y-4 pb-4">
                  <FormField
                    control={form.control as any}
                    name="document_url"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Document URL</FormLabel>
                        <FormControl>
                          <Input
                            type="url"
                            placeholder="https://..."
                            {...field}
                            value={field.value ?? ''}
                          />
                        </FormControl>
                        <FormDescription>
                          Link to the signed contract PDF
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control as any}
                    name="signed_at"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Signed At</FormLabel>
                        <FormControl>
                          <Input
                            type="datetime-local"
                            {...field}
                            value={field.value ?? ''}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control as any}
                    name="notes"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Notes</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Internal notes about this lease..."
                            className="min-h-[100px]"
                            {...field}
                            value={field.value ?? ''}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CollapsibleContent>
              </Collapsible>
            </div>
          </div>
        )}
      </form>
    </Form>
  )
}
